/* global LanguageModel */
import { useState, useEffect } from "react";
import {
    Sparkles,
    Trophy,
    X,
    Check,
    RefreshCw,
    Wand2,
    AlertTriangle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";



export default function LanguageGuessGame() {
    const [currentTheme, setCurrentTheme] = useState("");
    const [currentWord, setCurrentWord] = useState("");
    const [correctLanguage, setCorrectLanguage] = useState("");
    const [score, setScore] = useState(0);
    const [totalRounds, setTotalRounds] = useState(0);
    const [gameState, setGameState] = useState("setup");
    const [feedback, setFeedback] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState("");
    const [isApiAvailable, setIsApiAvailable] = useState(false);
    const [wordMeaning, setWordMeaning] = useState("");

    useEffect(() => {
        async function checkApiAvailability() {

            if (typeof LanguageModel !== 'undefined') {
                try {
                    const availability = await LanguageModel.availability();
                    if (availability !== 'unavailable') {
                        setIsApiAvailable(true);
                        setGameState("idle");
                    } else {

                        setIsApiAvailable(false);
                        setError(`Gemini Nano model is not available in your browser (Status: ${availability}). 
                        Please ensure you are using a compatible version of Chrome with the correct flags enabled.`);
                        setGameState("setup");
                    }
                } catch (e) {
                    setIsApiAvailable(false);
                    setError("Error checking Gemini Nano availability. Ensure Chrome flags are set and the component is updated.");
                    setGameState("setup");
                }
            } else {
                setIsApiAvailable(false);
                setError("LanguageModel API is not available. Please use a compatible version of Chrome (Canary/Dev) with Prompt API flags enabled.");
                setGameState("setup");
            }
        }
        checkApiAvailability();
    }, []);

    const languages = [
        { name: "Spanish", code: "es" },
        { name: "French", code: "fr" },
        { name: "German", code: "de" },
        { name: "Italian", code: "it" },
        { name: "Portuguese", code: "pt" },
        { name: "Urdu", code: "ur" },
        { name: "Arabic", code: "ar" },
        { name: "Hindi", code: "hi" },
        { name: "Japanese", code: "ja" },
        { name: "Chinese", code: "zh" },
    ];

    const themes = [
        "Animals",
        "Vehicles",
        "Food",
        "Colors",
        "Nature",
        "Sports",
        "Professions",
        "Weather",
    ];

    /*
     * Calls the Gemini Nano model locally via the Chrome Prompt API.
     */
    async function callGeminiNano(prompt) {
        if (typeof LanguageModel === 'undefined') {
            throw new Error("Gemini Nano API not available in this browser.");
        }

        const systemPrompt = "You are a language learning game assistant. Use languages like Chinese,Japanese,Hindi,Arabic,Urdu,Portuguese,Italian,German,French,Spanish  Your task is to select one language and a related word based on the theme.You must also include the English meaning of the word you should not repeating words and languages in a sequence You must ONLY respond with a single JSON object in the format: {\"theme\": \"[THEME]\", \"language\": \"[LANGUAGE_NAME]\", \"word\": \"[WORD]\" , \"meaning\": \"[ENGLISH-MEANING]\"}"; 

        const responseConstraint = {
            type: "object",
            properties: {
                theme: { type: "string" },
                language: { type: "string" },
                word: { type: "string" },
                meaning : {type : "string"}
            },
            required: ["theme", "language", "word", "meaning"]
        };

        let session;
        let responseText;

        try {
            // Get model parameters
            const params = await LanguageModel.params();

            // Create session with initial system prompt and parameters
            session = await LanguageModel.create({
                initialPrompts: [
                    { role: 'system', content: systemPrompt }
                ],
                temperature: Math.min(0.9, params.maxTemperature),
                topK: Math.min(40, params.maxTopK)
            });

            // Prompt with response constraint for structured JSON output
            responseText = await session.prompt(prompt, { responseConstraint });

            // Parse and return the JSON response
            return JSON.parse(responseText);

        } catch (err) {
            console.error("Error generating word with Gemini Nano:", err);

            throw new Error(err.message || "Error communicating with Gemini Nano. Check browser console for details.");
        } finally {
            // Clean up session
            if (session) {
                session.destroy();
            }
        }
    }

    async function startNewRound() {
        if (!isApiAvailable) {
            setError("Gemini Nano API is not ready. Please check setup steps.");
            return;
        }

        setIsGenerating(true);
        setGameState("playing");
        setFeedback("");
        setError("");

        try {
            const randomTheme = themes[Math.floor(Math.random() * themes.length)];

            const prompt = `Theme: ${randomTheme}. Pick ONE language from: Spanish, French, German, Italian, Portuguese, Urdu, Arabic, Hindi, Japanese, or Chinese. Provide a simple, common word in that language related to the theme.`;


            const response = await callGeminiNano(prompt);


            if (response && response.theme && response.language && response.word) {
                setCurrentTheme(response.theme.trim());
                setCorrectLanguage(response.language.trim());
                setCurrentWord(response.word.trim());
                setWordMeaning(response.meaning.trim());
            } else {
                setError("Error parsing AI response. The model did not return the expected JSON structure. Please try again.");
                setGameState("idle");
            }
        } catch (err) {
            console.error("Error generating word:", err);
            setError(err.message || "Error communicating with Gemini Nano. Please check console and browser setup.");
            setGameState("idle");
        }

        setIsGenerating(false);
    }

    function handleGuess(language) {
        if (gameState !== "playing") return;
        setTotalRounds(totalRounds + 1);
        const isCorrect = language.name.toLowerCase() === correctLanguage.toLowerCase();

        if (isCorrect) {
            setScore(score + 1);
            setFeedback(`Correct! "${currentWord}" is ${correctLanguage}!`);
        } else {
            setFeedback(
                `Not quite! "${currentWord}" is ${correctLanguage}, not ${language.name}.`
            );
        }

        setGameState("answered");
    }

    function resetGame() {
        setScore(0);
        setTotalRounds(0);
        setGameState(isApiAvailable ? "idle" : "setup");
        setFeedback("");
        setCurrentWord("");
        setCurrentTheme("");
        setError("");

    }

    const cardVariants = {
        hidden: { opacity: 0, y: 40, scale: 0.9 },
        visible: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: -40, scale: 0.9 },
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-zinc-600 to-rose-950 flex items-center justify-center p-6">
            <AnimatePresence mode="wait">
                {/* SETUP/INITIAL STATE - No API Key, checking Gemini Nano availability */}
                {gameState === "setup" && (
                    <motion.div
                        key="setup"
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        transition={{ duration: 0.5 }}
                        className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full relative overflow-hidden text-center"
                    >
                        <motion.div
                            className="absolute -top-10 -right-10 bg-stone-300 w-40 h-40 rounded-full opacity-30"
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ repeat: Infinity, duration: 3 }}
                        />
                        <Sparkles className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                        <h1 className="text-3xl font-extrabold text-gray-800 mb-2">
                            Language Guesser (Gemini Nano)
                        </h1>

                        {error ? (
                            <div className="text-left">
                                <p className="text-sm text-red-700 bg-red-100 border border-red-300 p-3 rounded-xl mb-4 flex items-start gap-2">
                                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </p>
                                <p className="text-gray-500 text-sm">
                                    This game uses the **Chrome Prompt API (Gemini Nano)** which requires:
                                    <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                                        <li>Chrome Dev/Canary (v127+)</li>
                                        <li>Specific Chrome flags enabled</li>
                                        <li>The model component to be downloaded</li>
                                    </ul>
                                    If you meet the requirements, try restarting Chrome.
                                </p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="w-full mt-6 py-3 bg-stone-600 text-white font-bold rounded-xl hover:scale-105 transition-transform"
                                >
                                    Retry Check
                                </button>
                            </div>
                        ) : (
                            <div className="text-center">
                                <p className="text-gray-500 mb-6">
                                    Checking browser compatibility for Gemini Nano...
                                </p>
                                <RefreshCw className="w-8 h-8 text-stone-600 mx-auto animate-spin" />
                            </div>
                        )}

                    </motion.div>
                )}

                {/* GAME SCREEN - When the API is available */}
                {isApiAvailable && gameState !== "setup" && (
                    <motion.div
                        key="game"
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        transition={{ duration: 0.5 }}
                        className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl p-8"
                    >
                        <div className="flex justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <Trophy className="text-yellow-500 w-8 h-8" />
                                <div>
                                    <p className="text-sm text-gray-500">Score</p>
                                    <h2 className="text-2xl font-bold text-gray-800">
                                        {score}/{totalRounds}
                                    </h2>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                {/* Removed Key button as API key is not needed */}
                                <button
                                    onClick={resetGame}
                                    className="bg-gray-100 p-2 rounded-lg hover:bg-gray-200 transition"
                                >
                                    <RefreshCw className="w-5 h-5 text-gray-700" />
                                </button>
                            </div>
                        </div>

                        <AnimatePresence mode="wait">
                            {gameState === "idle" && (
                                <motion.div
                                    key="idle"
                                    variants={cardVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="text-center py-12"
                                >
                                    <Wand2 className="w-16 h-16 text-stone-600 mx-auto mb-4 animate-bounce" />
                                    <h2 className="text-3xl font-bold text-gray-800 mb-3">
                                        Ready to Begin?
                                    </h2>
                                    <p className="text-gray-600 mb-6">
                                        Gemini Nano is ready to pick a random theme and word!
                                    </p>
                                    <button
                                        onClick={startNewRound}
                                        className="px-8 py-4 bg-gradient-to-r from-stone-600 to-stone-950 text-white text-xl font-semibold rounded-2xl hover:scale-105 transition-transform"
                                        disabled={isGenerating}
                                    >
                                        Start Round
                                    </button>
                                </motion.div>
                            )}

                            {isGenerating && (
                                <motion.div
                                    key="loading"
                                    variants={cardVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="text-center py-12"
                                >
                                    <Sparkles className="w-16 h-16 text-stone-600 mx-auto mb-4 animate-spin" />
                                    <p className="text-lg text-gray-600">AI is thinking...</p>
                                </motion.div>
                            )}

                            {gameState === "playing" && !isGenerating && (
                                <motion.div
                                    key="playing"
                                    variants={cardVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                >
                                    {error && (
                                        <p className="text-sm text-red-500 bg-red-50 p-2 rounded-xl mb-4">
                                            {error}
                                        </p>
                                    )}
                                    <div className="text-center mb-6">
                                        <p className="text-sm text-stone-500 font-semibold">
                                            Theme: {currentTheme}
                                        </p>
                                        <h1 className="text-5xl font-bold bg-gradient-to-r from-stone-600 to-stone-950 bg-clip-text text-transparent mt-3">
                                            {currentWord}
                                        </h1>
                                        <p className="text-gray-700 mt-4 text-lg font-medium">
                                            Which language is this?
                                        </p>
                                    </div>
                                    <div className="flex overflow-x-auto gap-4 pb-2 scrollbar-hide justify-center flex-wrap">
                                        {languages.map((lang) => (
                                            <motion.button
                                                key={lang.code}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={() => handleGuess(lang)}
                                                className="flex-shrink-0 px-6 py-3 rounded-2xl text-white font-semibold bg-gradient-to-r from-stone-500 to-stone-950 hover:shadow-lg transition-transform min-w-[120px]"
                                            >
                                                {lang.name}
                                            </motion.button>
                                        ))}
                                    </div>

                                </motion.div>
                            )}

                            {gameState === "answered" && (
                                <motion.div
                                    key="answered"
                                    variants={cardVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="text-center py-8"
                                >
                                    {feedback.includes("Correct") ? (
                                        <Check className="w-20 h-20 text-green-500 mx-auto mb-4" />
                                    ) : (
                                        <X className="w-20 h-20 text-red-500 mx-auto mb-4" />
                                    )}
                                    <p className="text-2xl font-bold text-gray-800 mb-6">
                                        {feedback}
                                    </p>
                                    <p className="text-lg text-gray-700 mb-4">
                                        Meaning: <span className="font-semibold text-stone-700">{wordMeaning}</span>
                                    </p>

                                    <button
                                        onClick={startNewRound}
                                        className="px-8 py-4 bg-gradient-to-r from-stone-600 to-stone-950 text-white text-xl font-semibold rounded-2xl hover:scale-105 transition-transform"
                                        disabled={isGenerating}
                                    >
                                        Next Round
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}